import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, ShoppingBag, X } from "lucide-react";
import { AnnouncementBar } from "@/components/layout/AnnouncementBar";
import { Logo } from "@/components/ui/Logo";
import { Button } from "@/components/ui/button";
import { useCart } from "@/lib/cart";
import { hasBuyablePaket } from "@/lib/paket-hemat";
import { usePaketHemat } from "@/lib/use-paket-hemat";
import { cn } from "@/lib/utils";

const navLinks = [
  { label: "Meja", to: "/katalog?jenis=meja" },
  { label: "Aksesori", to: "/katalog?jenis=aksesori" },
  { label: "Perakitan", to: "/perakitan" },
  { label: "Untuk kantor", to: "/kantor" },
  { label: "Tentang", to: "/tentang" },
];

/**
 * The red, sparkled "Paket Hemat" link, first in the nav, as the client asked.
 *
 * Only there while a Paket Hemat can be bought: a sale link to a page of sold
 * out bundles is worse than no link. It arrives after the catalogue loads, so
 * the prerendered header never has it and hydration never disagrees.
 */
function PaketHematLink({ className, sparkle }: { className: string; sparkle: string }) {
  return (
    <Link to="/paket-hemat" className={className}>
      <span className="relative">
        <span aria-hidden className={cn("absolute -left-2 -top-1.5 text-[9px]", sparkle)}>
          ✦
        </span>
        Paket Hemat
        <span aria-hidden className={cn("absolute -right-2 -top-1.5 text-[9px]", sparkle)}>
          ✦
        </span>
      </span>
    </Link>
  );
}

export function Header({
  overHero,
}: {
  /** Float over a hero, like the home page. A campaign page that opens on one. */
  overHero?: boolean;
} = {}) {
  const { pathname } = useLocation();
  const isHome = pathname === "/" || Boolean(overHero);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { count } = useCart();
  const paket = usePaketHemat();
  const showPaket = paket.status === "ready" && hasBuyablePaket(paket.list);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Solid = dark-on-white. The header only floats over a hero on the home
  // page; everywhere else it sits on white and needs the dark treatment from
  // the first pixel, or the logo disappears against the page.
  const solid = scrolled || open || !isHome;

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-all duration-300",
        solid
          ? "border-b border-ink/[0.07] bg-white/85 backdrop-blur-md"
          : "border-b border-transparent bg-gradient-to-b from-black/45 via-black/20 to-transparent",
      )}
    >
      {/* Inside the fixed header so it moves with it; the pages' top padding
          already clears a header this tall. */}
      <AnnouncementBar />
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:h-18 sm:px-8">
        <Link to="/" className="flex items-center" aria-label="Kanata — beranda">
          <Logo className={cn("h-6 w-auto transition-colors sm:h-7", solid ? "text-brand" : "text-white")} />
        </Link>

        <nav className="hidden items-center gap-6 lg:flex">
          {showPaket && (
            <PaketHematLink
              className={cn(
                "text-sm font-semibold transition-colors",
                solid ? "text-sale hover:text-sale/80" : "text-sale-bright hover:text-white",
              )}
              sparkle={solid ? "text-sale" : "text-sale-bright"}
            />
          )}
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={cn(
                "text-sm font-medium transition-colors",
                solid ? "text-ink/70 hover:text-brand" : "text-white/90 hover:text-white",
              )}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-1.5">
          <Link
            to="/keranjang"
            aria-label={count > 0 ? `Keranjang, ${count} item` : "Keranjang"}
            className={cn(
              "relative inline-flex size-10 items-center justify-center rounded-full transition-colors",
              solid ? "text-ink hover:bg-ink/[0.05]" : "text-white hover:bg-white/10",
            )}
          >
            <ShoppingBag className="size-5" />
            {count > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex size-5 items-center justify-center rounded-full bg-brand text-[11px] font-semibold text-white">
                {count > 9 ? "9+" : count}
              </span>
            )}
          </Link>

          <Button asChild size="sm" variant={solid ? "default" : "white"} className="hidden sm:inline-flex">
            <Link to="/katalog">Belanja</Link>
          </Button>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Tutup menu" : "Buka menu"}
            aria-expanded={open}
            className={cn(
              "inline-flex size-10 items-center justify-center rounded-full transition-colors lg:hidden",
              solid ? "text-ink hover:bg-ink/[0.05]" : "text-white hover:bg-white/10",
            )}
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="border-t border-ink/[0.07] bg-white lg:hidden">
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-5 py-4 sm:px-8">
            {showPaket && (
              <PaketHematLink
                className="rounded-xl px-5 py-3 text-base font-semibold text-sale transition-colors hover:bg-sale-tint"
                sparkle="text-sale"
              />
            )}
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="rounded-xl px-3 py-3 text-base font-medium text-ink/80 transition-colors hover:bg-brand-tint hover:text-brand"
              >
                {link.label}
              </Link>
            ))}
            <Button asChild size="lg" className="mt-2 w-full">
              <Link to="/katalog">Lihat semua meja</Link>
            </Button>
          </div>
        </nav>
      )}
    </header>
  );
}
